import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import dayjs, { Dayjs } from 'dayjs';
import styles from './TimelineView.module.scss';
import { VehicleReservationDto } from 'api/vehicleReservations/models/VehicleReservationDto';
import { ReservationsVehicleListItemDto } from 'api/vehicles/models/ReservationsVehicleListItemDto';
import { CalendarMode } from './CalendarView';
import { DATE_FORMAT_DEFAUT } from 'Config';
import ReactTooltip from 'react-tooltip';
import DateFormat from 'common/components/dateFormat/dateFormat';


export type TimelineRange = 7 | 10 | 30;

type Props = {
    mode: CalendarMode;
    items: VehicleReservationDto[];
    allVehicles?: ReservationsVehicleListItemDto[];
    // eslint-disable-next-line unused-imports/no-unused-vars
    isLoading: boolean;
    startDate: Dayjs;
    daysCount: TimelineRange;
    hasVehicleFilters?: boolean;
};

interface VehicleGroup {
    key: string;
    registrationNumber: string;
    brandName: string;
    modelName: string;
    photoUrl: string;
    items: VehicleReservationDto[];
}

const BAR_HEIGHT = 1.5; // rem
const BAR_GAP = 0.25; // rem
const BAR_PADDING = 0.5; // rem top/bottom in barsArea

const TimelineView: React.FC<Props> = ({ mode, items, allVehicles, isLoading, startDate, daysCount, hasVehicleFilters }) => {
    const { t } = useTranslation();

    const DAY_WIDTH_PERCENT = 100 / daysCount;

    const visibleDays = useMemo(() => {
        return Array.from({ length: daysCount }, (_, i) => startDate.add(i, 'day'));
    }, [startDate, daysCount]);

    // Build vehicle groups: start from all vehicles, then merge reservation items
    const vehicleGroups = useMemo(() => {
        const rangeStart = visibleDays[0];
        const rangeEnd = visibleDays[daysCount - 1];
        const map = new Map<string, VehicleGroup>();

        // Seed with all vehicles (empty items)
        if (allVehicles && allVehicles.length > 0) {
            allVehicles.forEach(v => {
                map.set(v.id, {
                    key: v.id,
                    registrationNumber: v.vehicleRegistrationNumber || '-',
                    brandName: v.vehicleBrandName || '',
                    modelName: v.vehicleModelName || '',
                    photoUrl: v.photoUrl || v.vehicleSegmentDefaultPhotoUrl || '',
                    items: [],
                });
            });
        }

        // Merge reservation/request items into matching vehicle groups
        items.forEach(item => {
            const start = dayjs(item.startDate);
            const end = dayjs(item.endDate);

            if (end.isBefore(rangeStart, 'day') || start.isAfter(rangeEnd, 'day')) return;

            // Skip items without an assigned vehicle when vehicle-specific filters are active
            const hasAssignedVehicle = !!(item.vehicleId || item.requestedVehicleId);
            if (hasVehicleFilters && !hasAssignedVehicle) return;

            const key = item.vehicleId || item.requestedVehicleId || item.vehicleRegistrationNumber || 'unknown';
            if (!map.has(key)) {
                map.set(key, {
                    key,
                    registrationNumber: item.vehicleRegistrationNumber || item.requestedVehicleRegistrationNumber || t('reservations.calendar.no_vehicle_assigned'),
                    brandName: item.vehicleBrandName || item.requestedVehicleBrandName || '',
                    modelName: item.vehicleModelName || item.requestedVehicleModelName || '',
                    photoUrl: item.vehiclePhotoUrl || item.requestedVehiclePhotoUrl || '',
                    items: [],
                });
            }
            map.get(key)!.items.push(item);
        });

        // Sort: vehicles with items first, then empty ones
        return Array.from(map.values()).sort((a, b) => {
            if (a.items.length > 0 && b.items.length === 0) return -1;
            if (a.items.length === 0 && b.items.length > 0) return 1;
            return a.registrationNumber.localeCompare(b.registrationNumber);
        });
    }, [items, allVehicles, visibleDays, daysCount, hasVehicleFilters]);

    const today = dayjs();

    // Calculate today's percentage position within the visible range
    const todayPercent = useMemo(() => {
        const rangeStart = visibleDays[0].startOf('day');
        const rangeEnd = visibleDays[daysCount - 1].endOf('day');
        if (today.isBefore(rangeStart) || today.isAfter(rangeEnd)) return null;
        const totalMs = rangeEnd.diff(rangeStart);
        const elapsedMs = today.diff(rangeStart);
        return (elapsedMs / totalMs) * 100;
    }, [today, visibleDays, daysCount]);

    const getBarPosition = (item: VehicleReservationDto, index: number) => {
        const start = dayjs(item.startDate);
        const end = dayjs(item.endDate);
        const rangeStart = visibleDays[0].startOf('day');
        const rangeEnd = visibleDays[daysCount - 1].endOf('day');

        const clampedStart = start.isBefore(rangeStart) ? rangeStart : start;
        const clampedEnd = end.isAfter(rangeEnd) ? rangeEnd : end;

        const startDayFraction = clampedStart.diff(rangeStart, 'minute') / (24 * 60);
        const endDayFraction = clampedEnd.diff(rangeStart, 'minute') / (24 * 60);

        const leftPercent = startDayFraction * DAY_WIDTH_PERCENT;
        const widthPercent = Math.max((endDayFraction - startDayFraction) * DAY_WIDTH_PERCENT, DAY_WIDTH_PERCENT * 0.15);

        return {
            left: `${leftPercent}%`,
            width: `${widthPercent}%`,
            top: `${BAR_PADDING + index * (BAR_HEIGHT + BAR_GAP)}rem`,
            height: `${BAR_HEIGHT}rem`,
        };
    };

    const getBarClassName = (item: VehicleReservationDto) => {
        if (mode === 'reservations') return `${styles.bar} ${styles.barReservation}`;
        if (mode === 'combined') {
            // In combined mode: reservations (no status or ACTIVE) get teal, pending requests get yellow
            if (item.status === 'PENDING_APPROVAL') return `${styles.bar} ${styles.barPending}`;
            return `${styles.bar} ${styles.barReservation}`;
        }
        switch (item.status) {
            case 'PENDING_APPROVAL': return `${styles.bar} ${styles.barPending}`;
            case 'APPROVED': return `${styles.bar} ${styles.barApproved}`;
            case 'REJECTED': return `${styles.bar} ${styles.barRejected}`;
            default: return `${styles.bar} ${styles.barReservation}`;
        }
    };

    const getBarsAreaHeight = (count: number) => {
        if (count === 0) return undefined;
        return `${BAR_PADDING * 2 + count * BAR_HEIGHT + Math.max(0, count - 1) * BAR_GAP}rem`;
    };

    const isWeekend = (day: Dayjs) => day.day() === 0 || day.day() === 6;

    const gridCols = `200px repeat(${daysCount}, 1fr)`;

    return (
        <div className={styles.container}>
            <div className={styles.timeline}>
                <div className={styles.dayHeadersRow} style={{ gridTemplateColumns: gridCols }}>
                    <div className={styles.vehicleColHeader}>{t('reservations.calendar.vehicle')}</div>
                    {visibleDays.map((day, i) => {
                        const isToday = today.isSame(day, 'day');
                        const isWknd = isWeekend(day);
                        const showMonth = day.date() === 1 || i === 0;
                        const headerClasses = [
                            styles.dayHeader,
                            isToday ? styles.todayHeader : '',
                            isWknd ? styles.weekendHeader : '',
                        ].filter(Boolean).join(' ');

                        return (
                            <div key={day.format('YYYY-MM-DD')} className={headerClasses}>
                                <span className={styles.dayHeaderWeekday}>{day.format('ddd')}</span>
                                <span className={styles.dayHeaderDate}>{day.format('DD')}</span>
                                {showMonth && <span className={styles.dayHeaderMonth}>{day.format('MMM')}</span>}
                            </div>
                        );
                    })}
                </div>

                <div className={styles.body}>
                    {vehicleGroups.length === 0 && !isLoading ? (
                        <div className={styles.emptyState}>
                            {mode === 'reservations' || mode === 'combined'
                                ? t('reservations.calendar.no_data_week')
                                : t('reservations.calendar.no_requests_week')
                            }
                        </div>
                    ) : vehicleGroups.map(group => (
                            <div key={group.key} className={styles.vehicleRow} style={{ gridTemplateColumns: gridCols }}>
                                <div className={styles.vehicleCol}>
                                    {group.photoUrl && (
                                        <div
                                            className={styles.vehicleImage}
                                            style={{ backgroundImage: `url(${group.photoUrl})` }}
                                        />
                                    )}
                                    <div className={styles.vehicleInfo}>
                                        <div className={styles.vehicleReg}>{group.registrationNumber}</div>
                                        {(group.brandName || group.modelName) && (
                                            <div className={styles.vehicleBrandModel}>
                                                {group.brandName}{group.brandName && group.modelName ? ' | ' : ''}{group.modelName}
                                            </div>
                                        )}
                                    </div>
                                </div>
                                <div
                                    className={styles.barsArea}
                                    style={{ minHeight: getBarsAreaHeight(group.items.length) }}
                                >
                                    {visibleDays.map((day, i) => {
                                        const isWknd = isWeekend(day);
                                        const isToday = today.isSame(day, 'day');
                                        const colClasses = [
                                            styles.dayColBg,
                                            isWknd ? styles.weekendCol : '',
                                            isToday ? styles.todayCol : '',
                                        ].filter(Boolean).join(' ');

                                        return (
                                            <div
                                                key={day.format('YYYY-MM-DD')}
                                                className={colClasses}
                                                style={{ left: `${i * DAY_WIDTH_PERCENT}%`, width: `${DAY_WIDTH_PERCENT}%` }}
                                            />
                                        );
                                    })}
                                    {todayPercent !== null && (
                                        <div className={styles.todayLine} style={{ left: `${todayPercent}%` }} />
                                    )}
                                    {group.items.map((item, idx) => {
                                        const barPos = getBarPosition(item, idx);
                                        const tooltipId = `tl-${group.key}-${idx}`;
                                        const regNumber = item.vehicleRegistrationNumber || item.requestedVehicleRegistrationNumber || '-';
                                        const brandName = item.vehicleBrandName || item.requestedVehicleBrandName || '';
                                        const modelName = item.vehicleModelName || item.requestedVehicleModelName || '';
                                        const startTime = item.startDate ? new Date(item.startDate).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }) : '';
                                        const endTime = item.endDate ? new Date(item.endDate).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }) : '';

                                        return (
                                            <React.Fragment key={item.id || idx}>
                                                <div
                                                    className={getBarClassName(item)}
                                                    style={barPos}
                                                    data-tip=""
                                                    data-for={tooltipId}
                                                >
                                                    <span className={styles.barLabel}>
                                                        {item.driverName || '-'}
                                                    </span>
                                                </div>
                                                <ReactTooltip id={tooltipId} place="top" effect="solid" className={styles.tooltip}>
                                                    <div className={styles.tooltipContent}>
                                                        <div className={styles.tooltipRow}>
                                                            <span className={styles.tooltipLabel}>{t('reservations.calendar.vehicle')}</span>
                                                            <span className={styles.tooltipValue}>
                                                                {regNumber}
                                                                {(brandName || modelName) && <> — {brandName}{brandName && modelName ? ' ' : ''}{modelName}</>}
                                                            </span>
                                                        </div>
                                                        {item.vehicleSegmentName && (
                                                            <div className={styles.tooltipRow}>
                                                                <span className={styles.tooltipLabel}>{t('vehicle.type')}</span>
                                                                <span className={styles.tooltipValue}>{item.vehicleSegmentName}</span>
                                                            </div>
                                                        )}
                                                        {(item.vehicleLocalNames || item.localName) && (
                                                            <div className={styles.tooltipRow}>
                                                                <span className={styles.tooltipLabel}>{t('reservations.calendar.agency')}</span>
                                                                <span className={styles.tooltipValue}>{item.vehicleLocalNames || item.localName}</span>
                                                            </div>
                                                        )}
                                                        {item.driverName && (
                                                            <div className={styles.tooltipRow}>
                                                                <span className={styles.tooltipLabel}>{t('reservations.calendar.driver')}</span>
                                                                <span className={styles.tooltipValue}>{item.driverName}</span>
                                                            </div>
                                                        )}
                                                        <div className={styles.tooltipRow}>
                                                            <span className={styles.tooltipLabel}>{t('reservations.calendar.dates')}</span>
                                                            <span className={styles.tooltipValue}>
                                                                <DateFormat value={item.startDate} format={DATE_FORMAT_DEFAUT} /> {startTime}
                                                                {' → '}
                                                                <DateFormat value={item.endDate} format={DATE_FORMAT_DEFAUT} /> {endTime}
                                                            </span>
                                                        </div>
                                                        {item.reason && (
                                                            <div className={styles.tooltipRow}>
                                                                <span className={styles.tooltipLabel}>{t('reservations.destination_reason')}</span>
                                                                <span className={styles.tooltipValue}>{item.reason}</span>
                                                            </div>
                                                        )}
                                                    </div>
                                                </ReactTooltip>
                                            </React.Fragment>
                                        );
                                    })}
                                </div>
                            </div>
                        ))}
                    </div>
                    <div className={styles.legend}>
                        <span className={styles.legendLabel}>{t('reservations.calendar.legend')}:</span>
                        <span className={styles.legendItem}>
                            <span className={styles.legendBadge} />
                            {t('reservations.calendar.legend_reservations')}
                        </span>
                        <span className={styles.legendItem}>
                            <span className={`${styles.legendBadge} ${styles.legendBadgeWarning}`} />
                            {t('reservations.calendar.legend_pending_requests')}
                        </span>
                    </div>
                </div>
        </div>
    );
};

export default TimelineView;
